import { P2GBox, P2GLink, P2GText, P2GIcon, P2GGrid, useTheme, SvgMapMarker, P2GDivider } from '@parcel2go/core-ui';
import { Suspense, lazy, useMemo, useState } from 'react';
import { Shop } from '~/lib/types/edge-api-types';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useDistance, useTenant } from '@parcel2go/npm-p2g-app';
import Loading from '~/components/core/progress/loading';
import { PublicSettings } from '~/lib/ui-serverless-settings';

type DrawerDropShopProps = {
  shop: Shop;
  quoteOffersPrintInStore: boolean;
};

const Map = lazy(() => import('~/components/core/map/map'));

function DrawerDropShop({ shop, quoteOffersPrintInStore }: DrawerDropShopProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { toDistance } = useDistance();
  const { settings } = useTenant();
  const theme = useTheme();
  const [showMap, setShowMap] = useState(false);

  const openingTimeMessage = useMemo(() => {
    if (!shop.openingTimesCollection?.length) {
      return null;
    }
    const today = new Date().getDay();
    const openingTimes = shop.openingTimesCollection[today];
    if (openingTimes) {
      return openingTimes.closingTime === '23:59' && ['00:00', '00:01'].includes(openingTimes.openingTime)
        ? t('DROP_SHOP_LIST_SHOP_OPEN_ALL_DAY', { defaultValue: 'Open all day' })
        : t('DROP_SHOP_LIST_SHOP_OPEN_UNTIL', {
            time: openingTimes.closingTime,
            defaultValue: 'Open until {{time}}'
          });
    }
    return t('DROP_SHOP_LIST_SHOP_CLOSED', { defaultValue: 'Closed' });
  }, [shop.openingTimesCollection, t]);

  const distance = useMemo(() => {
    return toDistance(shop.distance);
  }, [shop.distance, toDistance]);

  const map = useMemo(() => {
    return (
      <Map
        apiKey={settings[PublicSettings.MAPSKEY] ?? ''}
        center={{ lng: shop.longitude, lat: shop.latitude }}
        zoom={15}
        markers={[
          {
            position: { lng: shop.longitude, lat: shop.latitude },
            label: shop.name,
            color: theme.palette?.primary?.main
          }
        ]}
      />
    );
  }, [settings, shop.latitude, shop.longitude, shop.name, theme.palette?.primary?.main]);

  const showMapText = showMap
    ? t('HIDE_MAP_LINK', { defaultValue: 'Hide map' })
    : t('SHOW_MAP_LINK', { defaultValue: 'Show map' });

  return (
    <P2GBox>
      <P2GDivider />

      <P2GBox py={1}>
        <P2GGrid container spacing={1}>
          <P2GGrid item>
            <P2GIcon size="xs" palette="primary">
              <SvgMapMarker />
            </P2GIcon>
          </P2GGrid>

          <P2GGrid item xs>
            <P2GBox mb={0.5}>
              <P2GText fontWeight="bold" variant="body1" component="p">
                {shop.name}
              </P2GText>
            </P2GBox>

            <P2GBox mb={0.5}>
              <P2GText variant="body2" color="textSecondary">
                {t('DROP_SHOP_ADDRESS', {
                  defaultValue: '{{property}}, {{street}} {{postcode}}',
                  property: shop.address1,
                  street: shop.address2 ? shop.address2 + ',' : '',
                  postcode: shop.postcode
                })}
              </P2GText>
            </P2GBox>

            <P2GBox>
              <P2GBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <P2GText variant="body2">
                  <Trans
                    i18nKey="DROP_SHOP_TIME"
                    t={t}
                    components={[
                      <P2GLink
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMap((prev) => !prev);
                        }}
                      />
                    ]}
                    values={{ openingTimeMessage, distance, showMapText }}
                    defaults={
                      openingTimeMessage
                        ? '{{openingTimeMessage}} - {{distance}} - <0>{{showMapText}}</0>'
                        : '{{distance}} - <0>{{showMapText}}</0>'
                    }
                  />
                </P2GText>
              </P2GBox>
            </P2GBox>

            {quoteOffersPrintInStore && (
              <P2GBox mt={0.5}>
                <P2GText variant="body2">{t('DROP_SHOP_PRINT_IN_STORE', { defaultValue: 'Print in shop:' })} </P2GText>
                <P2GText
                  component="span"
                  variant="body2"
                  sx={{
                    color: shop.printInStoreAvailable ? theme.palette.success.main : theme.palette.text.secondary
                  }}
                >
                  {shop.printInStoreAvailable
                    ? t('DROP_SHOP_PRINT_IN_STORE_AVAILABLE', { defaultValue: 'Available' })
                    : t('DROP_SHOP_PRINT_IN_STORE_UNAVAILABLE', { defaultValue: 'Unavailable' })}
                </P2GText>
              </P2GBox>
            )}

            {showMap && (
              <Suspense fallback={<Loading />}>
                <P2GBox mt={1}>{map}</P2GBox>
              </Suspense>
            )}
          </P2GGrid>
        </P2GGrid>
      </P2GBox>

      <P2GDivider />
    </P2GBox>
  );
}

export default function DropshopListCompact({
  shops,
  quoteOffersPrintInStore = false
}: {
  shops: Shop[];
  quoteOffersPrintInStore?: boolean;
}) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);

  if (!shops.length) {
    return (
      <P2GText variant="body2" color="textSecondary">
        {t('NO_SHOPS', { defaultValue: 'No shops found' })}
      </P2GText>
    );
  }

  return (
    <P2GBox>
      {shops.map((shop) => (
        <DrawerDropShop key={shop.id} shop={shop} quoteOffersPrintInStore={quoteOffersPrintInStore} />
      ))}
    </P2GBox>
  );
}
